import { useDocument, useStructuredWorkflow } from "@/api/workflow-hooks";
import { Checkbox } from "@/components/checkbox";
import { FileUploadButton } from "@/components/file-upload-button";
import type { ExportExcelConfig } from "@repo/shared/workflow";
import { type WorkBook, read } from "@sheet/coredemo";
import { produce } from "immer";
import { useEffect, useState } from "react";
import { ExcelTemplateEditor } from "./ExcelTemplateEditor";

type ExcelEditorProps = {
  workflowId: string;
  actionId: string;
  definition: ExportExcelConfig;
  onConfigChange?: (config: ExportExcelConfig) => void;
  onFileUpload?: (file: File) => void;
};

const ExcelEditor = ({
  workflowId,
  actionId,
  definition: config,
  onConfigChange,
  onFileUpload,
}: ExcelEditorProps) => {
  const { data: docResponse } = useDocument(
    workflowId,
    config.templateDocumentId,
  );
  const { document, signedUrl } = docResponse ?? {};

  const [rawBytes, setRawBytes] = useState<ArrayBuffer | null>(null);
  const [workbook, setWorkbook] = useState<WorkBook | null>(null);

  const { aggregateModel } = useStructuredWorkflow(workflowId);

  useEffect(() => {
    const fn = async () => {
      if (!signedUrl) return;

      // Fetch the XLSX file from a URL
      const response = await fetch(signedUrl);
      const arrayBuffer = await response.arrayBuffer();
      setRawBytes(arrayBuffer);

      // Parse the workbook; cellStyles must be true if you want style info
      const wb = read(arrayBuffer, {
        cellStyles: true,
      });

      // Set workbook in local state
      setWorkbook(wb);
    };

    fn();
  }, [signedUrl]);

  return (
    <div className="overflow-y-auto">
      <div className="py-2 px-4 border rounded-lg mb-4 flex flex-col gap-2">
        <div className="flex items-center justify-between">
          {document && (
            <p className="font-mono text-xs bg-secondary px-1.5 py-1 rounded">
              {document?.fileName}
            </p>
          )}
          <div className="flex items-center space-x-2 py-2">
            {/* {definition.template_url && (
              <Button
                variant="outline"
                onClick={() => {
                  // extract
                  extractTemplateMut.mutateAsync();
                }}
                loading={extractTemplateMut.isPending}
              >
                <MagicWandIcon className="w-4 h-4 mr-2" />
                Auto-extract
              </Button>
            )} */}
            <FileUploadButton
              text={config.templateDocumentId ? "Re-upload" : "Upload"}
              variant={config.templateDocumentId ? "outline" : "default"}
              onUpload={async (files) => {
                // Mutate with your custom Upload logic
                if (files.length > 0) {
                  onFileUpload?.(files[0]);
                }
              }}
            />
          </div>
        </div>
        {config.editTemplate && (
          <div>
            <p className="text-sm font-medium mb-1">Include sheets</p>
            <div className="flex flex-col gap-1">
              {workbook?.SheetNames.map((name) => (
                <div key={name} className="flex items-center space-x-2">
                  <Checkbox
                    value={name}
                    checked={
                      config.editTemplate.sheets.find(
                        (sheet) => sheet.name === name,
                      )?.enabled ?? false
                    }
                    onCheckedChange={(checked) => {
                      const newConfig = produce(config, (draft) => {
                        const sheet = draft.editTemplate.sheets.find(
                          (sheet) => sheet.name === name,
                        );

                        if (sheet) {
                          sheet.enabled = !!checked;
                        }
                      });
                      onConfigChange?.(newConfig);
                    }}
                  />
                  <p>{name}</p>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
      {rawBytes && (
        <ExcelTemplateEditor
          rawBytes={rawBytes}
          config={config}
          onConfigChange={onConfigChange}
          model={aggregateModel}
        />
      )}
    </div>
  );
};

export default ExcelEditor;
